/* eslint-disable no-underscore-dangle */
import * as csc from 'cornerstone-core';
import {
  EVENTS,
  BaseAnnotationTool,
  store,
  clipToBox,
  triggerEvent,
  removeToolState,
  anyHandlesOutsideImage,
  getToolForElement,
} from 'app/CornerstoneTools';
import mouseButtonMaskToButtons from 'app/CornerstoneTools/mouseButtonMaskToButtons';
import { EventWithCornerstoneDetail, InteractionType } from 'app/CornerstoneTools/types';
import { AnnotationHandle, BaseAnnotation, ToolOptions } from 'cornerstone-tools';

const _moveEvents = {
  mouse: [EVENTS.MOUSE_MOVE, EVENTS.MOUSE_DRAG],
  touch: [EVENTS.TOUCH_DRAG],
};

const _moveEndEvents = {
  mouse: [EVENTS.MOUSE_UP, EVENTS.MOUSE_CLICK],
  touch: [EVENTS.TOUCH_END, EVENTS.TOUCH_PINCH, EVENTS.TAP],
};

type DoneMovingCallback = (success: boolean) => void;

/**
 * Stop the CornerstoneToolsTouchStart event from
 * Becoming a CornerstoneToolsTouchStartActive event when
 * MoveNewHandle ends
 */
function stopImmediatePropagation(evt: Event): boolean {
  evt.stopImmediatePropagation();
  return false;
}

/**
 * Move a new handle
 */
export default function moveNewHandle<ToolAnnotation extends BaseAnnotation>(
  element: HTMLElement,
  toolName: string,
  annotation: ToolAnnotation,
  handle: AnnotationHandle,
  toolOptions: ToolOptions,
  interactionType: InteractionType = 'mouse',
  doneMovingCallback?: DoneMovingCallback
): () => void {
  // Use global defaults, unless overidden by provided options
  const options: Required<ToolOptions> = {
    deleteIfHandleOutsideImage: store.state.deleteIfHandleOutsideImage,
    preventHandleOutsideImage: store.state.preventHandleOutsideImage,
    hasMoved: false,
    mouseButtonMask: 1,
    ...toolOptions,
  };

  const moveHandleCoords = (evt: EventWithCornerstoneDetail) => {
    const { currentPoints, image } = evt.detail;
    const { page } = currentPoints;
    const fingerOffset = -57;
    const targetLocation = csc.pageToPixel(
      element,
      interactionType === 'touch' ? page.x + fingerOffset : page.x,
      interactionType === 'touch' ? page.y + fingerOffset : page.y
    );
    handle.x = targetLocation.x;
    handle.y = targetLocation.y;

    if (options.preventHandleOutsideImage) {
      clipToBox(handle, image);
    }
  };

  let cancelFn: () => void;

  /**
   * Updates annotation as the "pointer" is moved/dragged
   * Emits `cornerstonetoolsmeasurementmodified` events
   */
  const moveHandler = (evt: EventWithCornerstoneDetail) => {
    const { image } = evt.detail;

    options.hasMoved = true;

    moveHandleCoords(evt);

    annotation.invalidated = true;
    handle.active = true;

    csc.updateImage(element);

    const activeTool = getToolForElement(element, toolName);
    if (activeTool.mode !== 'active') {
      cancelFn();
      return;
    }

    if (activeTool instanceof BaseAnnotationTool) {
      activeTool.updateCachedStats(image, element, annotation);
    }

    const eventType = EVENTS.MEASUREMENT_MODIFIED;
    const modifiedEventData = {
      toolName,
      toolType: toolName, // Deprecation notice: toolType will be replaced by toolName
      element,
      measurementData: annotation,
    };

    triggerEvent(element, eventType, modifiedEventData);
  };

  let moveEndHandler: (evt: EventWithCornerstoneDetail) => void;

  const endHandler = (success: boolean = true) => {
    // Remove event listeners
    _moveEvents[interactionType].forEach((eventType) => {
      element.removeEventListener(eventType, moveHandler as EventListener);
    });
    _moveEndEvents[interactionType].forEach((eventType) => {
      element.removeEventListener(eventType, moveEndHandler as EventListener);
    });
    element.removeEventListener(EVENTS.TOUCH_START, stopImmediatePropagation as EventListener);

    if (typeof doneMovingCallback === 'function') {
      doneMovingCallback(success);
    }

    // Update Image
    csc.updateImage(element);
  };

  moveEndHandler = (evt: EventWithCornerstoneDetail<MouseEvent>) => {
    const eventData = evt.detail;
    let moveNewHandleSuccessful = true;

    moveHandleCoords(evt);

    if (options.hasMoved === false) {
      return;
    }
    const tool = getToolForElement(element, toolName);

    if (!mouseButtonMaskToButtons(tool.options.mouseButtonMask).includes(eventData.event.button))
      return;

    // "Release" the handle
    annotation.active = false;
    annotation.invalidated = true;
    handle.active = false;
    handle.moving = false;

    // If any handle is outside the image, delete the tool data
    if (
      options.deleteIfHandleOutsideImage &&
      anyHandlesOutsideImage(evt.detail, annotation.handles)
    ) {
      annotation.cancelled = true;
      moveNewHandleSuccessful = false;
      removeToolState(element, toolName, annotation);
    }

    endHandler(moveNewHandleSuccessful);
  };

  cancelFn = () => endHandler(false);

  // Add event listeners
  _moveEvents[interactionType].forEach((eventType) => {
    element.addEventListener(eventType, moveHandler as EventListener);
  });
  element.addEventListener(EVENTS.TOUCH_START, stopImmediatePropagation as EventListener);

  _moveEndEvents[interactionType].forEach((eventType) => {
    element.addEventListener(eventType, moveEndHandler as EventListener);
  });

  annotation.active = true;

  handle.moving = true;
  handle.active = true;

  return cancelFn;
}
